// src/hooks/useConversations.js
import { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders.js';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export const useConversations = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchConversations = useCallback(async ({
    status = 'all',
    dateFrom = null,
    dateTo = null,
    search = null,
    limit = 20,
    offset = 0
  } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();

      // Build query string
      const params = new URLSearchParams();
      if (status !== 'all') params.append('status', status);
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      if (search) params.append('search', search);
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await fetch(`${BASE_URL}/api/training/conversations?${params}`, {
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch conversations');
      }

      const data = await response.json();
      setConversations(data.conversations);
      return data; // Return full data including pagination
    } catch (err) {
      setError(err.message);
      console.error('Error fetching conversations:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getConversationById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${BASE_URL}/api/training/conversations/${id}`, {
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch conversation');
      }

      return await response.json();
    } catch (err) {
      setError(err.message);
      console.error('Error fetching conversation:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const approveConversation = useCallback(async (id, trainingNotes = null) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${BASE_URL}/api/training/conversations/${id}/approve`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ training_notes: trainingNotes })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to approve conversation');
      }

      const updatedConversation = await response.json();

      // Update local state
      setConversations(prev =>
        prev.map(c => c.id === id ? updatedConversation : c)
      );

      return updatedConversation;
    } catch (err) {
      setError(err.message);
      console.error('Error approving conversation:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const removeApproval = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${BASE_URL}/api/training/conversations/${id}/approve`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove approval');
      }

      const updatedConversation = await response.json();

      // Update local state
      setConversations(prev =>
        prev.map(c => c.id === id ? updatedConversation : c)
      );

      return updatedConversation;
    } catch (err) {
      setError(err.message);
      console.error('Error removing approval:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Buscar mensagens de multiplas conversas (para historico completo por telefone)
  const getMessagesByConversationIds = useCallback(async (conversationIds) => {
    if (!conversationIds || conversationIds.length === 0) return [];

    setError(null);
    try {
      const headers = await getAuthHeaders();

      // Buscar todas as conversas em paralelo
      const promises = conversationIds.map(id =>
        fetch(`${BASE_URL}/api/training/conversations/${id}`, {
          headers,
          credentials: 'include'
        }).then(res => res.ok ? res.json() : null)
      );

      const results = await Promise.all(promises);

      // Combinar todas as mensagens e ordenar por timestamp
      const allMessages = results
        .filter(r => r !== null)
        .flatMap(conv => conv.messages || [])
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      return allMessages;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching messages by conversation ids:', err);
      throw err;
    }
  }, []);

  // Buscar TODAS as conversas sem limite para agrupamento por telefone
  const fetchAllConversations = useCallback(async ({
    status = 'all',
    search = null
  } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();

      // Buscar com limite alto para pegar todas
      const params = new URLSearchParams();
      if (status !== 'all') params.append('status', status);
      if (search) params.append('search', search);
      params.append('limit', 1000); // Limite alto para pegar todas
      params.append('offset', 0);

      const response = await fetch(`${BASE_URL}/api/training/conversations?${params}`, {
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch conversations');
      }

      const data = await response.json();
      setConversations(data.conversations);
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching all conversations:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllConversations(); // Buscar todas por padrao
  }, [fetchAllConversations]);

  return {
    conversations,
    loading,
    error,
    fetchConversations,
    fetchAllConversations,
    getConversationById,
    getMessagesByConversationIds,
    approveConversation,
    removeApproval
  };
};
